import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: 'Supervisor' | 'Admin' | 'Superadmin';
  department?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSignal = signal<CurrentUser | null>(this.readUser());
  private validationInFlight = false;
  private sessionHeartbeatId?: number;

  constructor(private api: ApiService, private router: Router) {
    window.addEventListener('storage', (event) => {
      if (event.key === 'pms_logout_event' && event.newValue) {
        this.clearSession();
        this.router.navigateByUrl('/login');
      }
    });
  }

  currentUser = this.userSignal.asReadonly();

  /**
   * Authenticates a user with email and password.
   * @param email Login email address.
   * @param password Plain-text password entered by the user.
   * @returns Observable containing JWT and public user profile.
   */
  login(email: string, password: string) {
    return this.api.post<{ token: string; user: CurrentUser }>('/auth/login', { email, password });
  }

  /**
   * Validates the stored JWT with the backend and refreshes the current-user signal.
   * @returns Observable resolving true only when the token and backend user are valid.
   */
  validateSession() {
    const token = this.token();
    if (!token) {
      this.clearSession();
      return of(false);
    }

    this.validationInFlight = true;
    return this.api.get<{ user: CurrentUser }>('/auth/me').pipe(
      tap(({ user }) => {
        const current = this.currentUser();
        if (current?.id === user.id && current.role !== user.role) {
          this.validationInFlight = false;
          this.forceLogout();
          return;
        }
        localStorage.setItem('pms_user', JSON.stringify(user));
        this.userSignal.set(user);
        this.validationInFlight = false;
      }),
      map(() => Boolean(this.token())),
      catchError(() => {
        this.validationInFlight = false;
        this.logout();
        return of(false);
      })
    );
  }

  /**
   * Requests a password reset email for an active account.
   * @param email Account email address.
   * @returns Observable with the backend status message.
   */
  forgotPassword(email: string) {
    return this.api.post<{ message: string }>('/auth/forgot-password', { email });
  }

  /**
   * Checks whether a reset token is still valid before allowing password entry.
   * @param token Raw reset token from the route.
   * @returns Observable with validity and associated email.
   */
  validateResetToken(token: string) {
    return this.api.get<{ valid: boolean; email: string }>(`/auth/reset-password/${token}`);
  }

  /**
   * Submits a new password for a valid reset token.
   * @param token Raw reset token from the route.
   * @param password New password.
   * @param confirmPassword Confirmation password.
   * @returns Observable with the reset result message.
   */
  resetPassword(token: string, password: string, confirmPassword: string) {
    return this.api.post<{ message: string }>(`/auth/reset-password/${token}`, { password, confirmPassword });
  }

  /**
   * Persists authenticated session data and updates the user signal.
   * @param token JWT issued by the backend.
   * @param user Public current-user payload.
   * @returns void.
   */
  completeLogin(token: string, user: CurrentUser) {
    localStorage.setItem('pms_token', token);
    localStorage.setItem('pms_user', JSON.stringify(user));
    this.userSignal.set(user);
    this.startSessionHeartbeat();
  }

  /**
   * Reads the current JWT from browser storage.
   * @returns Stored JWT or null.
   */
  token() {
    return localStorage.getItem('pms_token');
  }

  /**
   * Checks whether the browser currently has a JWT.
   * @returns True when a token is stored.
   */
  isLoggedIn() {
    return Boolean(this.token());
  }

  /**
   * Starts periodic backend validation so role/session changes are enforced even if realtime is missed.
   * @returns void.
   */
  startSessionHeartbeat() {
    if (this.sessionHeartbeatId || !this.token()) return;
    this.sessionHeartbeatId = window.setInterval(() => {
      if (!this.token() || this.validationInFlight) return;
      this.validateSession().subscribe();
    }, 15000);
  }

  /**
   * Checks whether a route guard can trust local auth state before backend validation.
   * @returns True when a token is present and validation is already running.
   */
  isValidating() {
    return this.validationInFlight;
  }

  /**
   * Verifies whether the current user has one of the required roles.
   * @param roles Allowed roles.
   * @returns True when the current role is included.
   */
  hasRole(...roles: CurrentUser['role'][]) {
    const user = this.currentUser();
    return Boolean(user && roles.includes(user.role));
  }

  /**
   * Clears local authentication state and redirects the user to login.
   * @returns void.
   */
  logout(message?: string) {
    this.clearSession(true);
    if (message) this.router.navigate(['/login'], { queryParams: { message } });
    else this.router.navigateByUrl('/login');
  }

  /**
   * Forces this browser session to re-authenticate and notifies other tabs.
   * @returns void.
   */
  forceLogout(message = 'Session expired. Please sign in again.') {
    this.logout(message);
  }

  /**
   * Logs out this session when a backend invalidation event targets the current user.
   * @param payload User identity carried by the realtime invalidation event.
   * @returns void.
   */
  handleSessionInvalidated(payload: { userId?: string; email?: string } | null | undefined) {
    const current = this.currentUser();
    if (!current || !payload) return;
    if ((payload.userId && payload.userId === current.id) || (payload.email && payload.email === current.email)) {
      this.forceLogout('Session expired. Please sign in again.');
    }
  }

  /**
   * Clears all browser-side session storage used by PMS.
   * @param broadcast Whether other browser tabs should also clear their session.
   * @returns void.
   */
  clearSession(broadcast = false) {
    localStorage.removeItem('pms_token');
    localStorage.removeItem('pms_user');
    if (broadcast) localStorage.setItem('pms_logout_event', String(Date.now()));
    if (this.sessionHeartbeatId) {
      window.clearInterval(this.sessionHeartbeatId);
      this.sessionHeartbeatId = undefined;
    }
    sessionStorage.clear();
    this.userSignal.set(null);
  }

  /**
   * Restores the current user from browser storage during service construction.
   * @returns Current user payload or null when not authenticated.
   */
  private readUser(): CurrentUser | null {
    try {
      const raw = localStorage.getItem('pms_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      localStorage.removeItem('pms_token');
      localStorage.removeItem('pms_user');
      sessionStorage.clear();
      return null;
    }
  }
}
